/**
 * Axios Security Configuration
 *
 * Implements SSRF protection by validating URLs before making requests.
 * This prevents attackers from bypassing baseURL by passing absolute URLs.
 *
 * CVE Reference: axios < 0.30.0 SSRF vulnerability
 * When baseURL is set, passing absolute URLs would ignore baseURL and send
 * requests to attacker-controlled hosts, potentially leaking credentials.
 */

/**
 * Whitelist of allowed external domains for specific operations
 * (e.g., AWS S3 for file uploads)
 */
const ALLOWED_EXTERNAL_DOMAINS = [
    's3.amazonaws.com',
    '.s3.amazonaws.com',
];

/**
 * Validates that a URL is relative or matches the allowed base URL
 * @param {string} url - The URL to validate
 * @param {string} baseURL - The expected base URL
 * @returns {boolean} - True if URL is safe, false otherwise
 */
export function isUrlSafe(url, baseURL = null) {
    if (!url) {
        return true;
    }

    // Allow relative URLs (don't start with http:// or https://)
    if (!url.match(/^https?:\/\//i)) {
        return true;
    }

    try {
        const urlObj = new URL(url);
        
        const hostname = urlObj.hostname;
        const isWhitelisted = ALLOWED_EXTERNAL_DOMAINS.some(domain => {
            if (domain.startsWith('.')) {
                return hostname.endsWith(domain.slice(1)) || hostname === domain.slice(1);
            }
            return hostname === domain || hostname.endsWith('.' + domain);
        });
        
        if (isWhitelisted) {
            return true;
        }
        
        const expectedBase = baseURL || window.location.origin;
        const baseObj = new URL(expectedBase);

        // URL must have the same origin as baseURL
        return urlObj.origin === baseObj.origin;
    } catch (e) {
        // If URL parsing fails, reject it
        return false;
    }
}

/**
 * Validates URL and logs potential SSRF attempts
 * @param {string} url - The URL to validate
 * @param {string} baseURL - The expected base URL
 * @throws {Error} - If URL validation fails
 */
export function validateUrl(url, baseURL = null) {
    if (!isUrlSafe(url, baseURL)) {
        const error = new Error(
            `SSRF Protection: Blocked request to absolute URL "${url}". ` +
            `Expected requests to be relative or match base URL "${baseURL || window.location.origin}".`
        );

        // Log to console for debugging
        console.error('[SSRF Protection]', error.message, {
            blockedUrl: url,
            expectedBase: baseURL || window.location.origin,
            timestamp: new Date().toISOString()
        });

        // You could also send this to your monitoring service
        // window.Sentry?.captureException(error);

        throw error;
    }
}

/**
 * Creates an axios request interceptor that validates URLs for SSRF protection
 * @param {object} axios - The axios instance
 * @param {string} baseURL - The expected base URL (optional, uses axios.defaults.baseURL)
 */
export function addSsrfProtection(axios, baseURL = null) {
    const effectiveBaseURL = baseURL || axios.defaults.baseURL;

    axios.interceptors.request.use(
        (config) => {
            // Validate the URL before making the request
            validateUrl(config.url, effectiveBaseURL);
            return config;
        },
        (error) => {
            return Promise.reject(error);
        }
    );
}
